import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { http, API } from "@/lib/api";

const STORAGE_KEY = "forte2_assessment_attempt_v2";
const AVG_SECS_PER_Q = 8;

const LIKERT_OPTIONS = [
  { v: 0, label: "None" },
  { v: 1, label: "Basic" },
  { v: 2, label: "Working" },
  { v: 3, label: "Strong" },
  { v: 4, label: "Expert" },
];

function loadStored() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch { return null; }
}

function saveStored(payload) {
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(payload)); } catch {}
}

function clearStored() {
  try { localStorage.removeItem(STORAGE_KEY); } catch {}
}

function fmtTime(sec) {
  if (sec <= 0) return "0 min";
  if (sec < 60) return `${sec}s`;
  return `~${Math.ceil(sec / 60)} min`;
}

export default function Assessment() {
  const nav = useNavigate();
  const [bank, setBank] = useState(null);          // questions bank from server
  const [attempt, setAttempt] = useState(null);    // { attempt_id, session_id, answers, profile }
  const [step, setStep] = useState(0);             // 0=profile, 1..N=questions, N+1=submit
  const [profile, setProfile] = useState({ background: "", goals: "" });
  const [answers, setAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [err, setErr] = useState("");
  const [ratelimited, setRatelimited] = useState(false);
  const saveTimer = useRef(null);
  const bootedRef = useRef(false);

  // Boot: fetch questions, then create OR resume an attempt
  useEffect(() => {
    if (bootedRef.current) return;
    bootedRef.current = true;
    (async () => {
      try {
        const b = (await http.get("/assessment/individual/questions")).data;
        setBank(b);
        // Resume?
        const stored = loadStored();
        if (stored?.attempt_id) {
          try {
            const a = (await http.get(`/assessment/attempts/${stored.attempt_id}`)).data;
            if (a && !a.completed_at) {
              setAttempt(a);
              setAnswers(a.answers || {});
              setProfile({ background: a.profile?.background || "", goals: a.profile?.goals || "" });
              // Jump to first unanswered question, or profile if profile empty
              if (!a.profile?.background) {
                setStep(0);
              } else {
                const firstUnanswered = b.questions.findIndex((q) => a.answers?.[q.id] === undefined);
                setStep(firstUnanswered === -1 ? b.questions.length + 1 : firstUnanswered + 1);
              }
              return;
            }
            // If it was completed and has report_id, redirect
            if (a?.completed_at && a?.report_id) {
              clearStored();
              nav(`/assessment/results/${a.report_id}`, { replace: true });
              return;
            }
          } catch { /* fall through to create */ }
        }
        // Fresh attempt
        const created = await http.post("/assessment/attempts", { session_id: stored?.session_id });
        const a = created.data;
        setAttempt(a);
        saveStored({ attempt_id: a.attempt_id, session_id: a.session_id });
      } catch (e) {
        if (e?.response?.status === 429) {
          setRatelimited(true);
        } else {
          setErr("Could not start the assessment. Please try again.");
        }
      }
    })();
  }, [nav]);

  const totalQ = bank?.questions.length || 0;
  const inQuestions = step >= 1 && step <= totalQ;
  const currentIdx = step - 1;
  const currentQ = inQuestions ? bank.questions[currentIdx] : null;
  const answeredCount = useMemo(
    () => bank ? bank.questions.filter((q) => answers[q.id] !== undefined).length : 0,
    [answers, bank]
  );
  const remainingSec = totalQ ? (totalQ - answeredCount) * (bank?.estimated_seconds_per_q || AVG_SECS_PER_Q) : 0;
  const progress = totalQ === 0 ? 0
    : step === 0 ? 5
    : inQuestions ? 5 + Math.round((answeredCount / totalQ) * 90)
    : 100;

  // Persist answers + profile to server (debounced)
  const persist = useCallback((nextAnswers, nextProfile) => {
    if (!attempt) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      try {
        await http.patch(`/assessment/attempts/${attempt.attempt_id}`, {
          answers: nextAnswers,
          profile: nextProfile,
        });
      } catch { /* offline-tolerant; localStorage still has answers */ }
    }, 400);
  }, [attempt]);

  const handleAnswer = (qid, val) => {
    const next = { ...answers, [qid]: val };
    setAnswers(next);
    persist(next, profile);
    // Auto-advance shortly after selection for a fluid flow
    setTimeout(() => setStep((s) => s + 1), 220);
  };

  const handleProfileNext = () => {
    persist(answers, profile);
    setStep(1);
  };

  const submit = async () => {
    setSubmitting(true);
    setErr("");
    try {
      // Final flush of any pending patch
      if (saveTimer.current) { clearTimeout(saveTimer.current); saveTimer.current = null; }
      await http.patch(`/assessment/attempts/${attempt.attempt_id}`, { answers, profile });
      const { data } = await http.post(`/assessment/attempts/${attempt.attempt_id}/complete`);
      clearStored();
      nav(`/assessment/results/${data.report_id}`);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not complete the assessment. Try again.");
      setSubmitting(false);
    }
  };

  if (ratelimited) {
    return (
      <div className="bg-[color:var(--f2-bg)] min-h-screen text-white flex flex-col">
        <Nav />
        <div className="flex-1 flex items-center justify-center px-6">
          <div className="max-w-md text-center hard-border p-10 bg-[color:var(--f2-surface)]">
            <div className="label-xs saffron mb-4">§ Rate Limit</div>
            <h1 className="font-display uppercase tracking-tighter font-black text-3xl">Too many attempts from your network.</h1>
            <p className="mt-4 text-[color:var(--f2-muted)]">Please try again in about an hour.</p>
            <Link to="/" className="btn-crisp mt-6 inline-flex">Back to home</Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="bg-[color:var(--f2-bg)] min-h-screen text-white flex flex-col">
      <Nav />
      <div className="flex-1 max-w-[900px] w-full mx-auto px-6 md:px-10 py-12">
        <div className="flex items-baseline justify-between mb-2">
          <div className="label-xs">§ Forte Score · Individuals</div>
          <div className="label-xs text-[color:var(--f2-muted)]">
            {inQuestions
              ? <>Question <span className="saffron">{currentIdx + 1}</span> / {totalQ} · {fmtTime(remainingSec)} left</>
              : step === 0
                ? "Step 1 of 2 · Tell us about you"
                : "Step 2 of 2 · Ready to submit"}
          </div>
        </div>

        <h1 className="font-display uppercase tracking-tighter font-black text-3xl md:text-5xl mb-6">
          {step === 0 && <>Tell us about <span className="saffron">you.</span></>}
          {inQuestions && currentQ && <span className="saffron">{currentQ.dim}</span>}
          {step === totalQ + 1 && <>Ready to <span className="saffron">launch.</span></>}
        </h1>

        {/* Progress */}
        <div className="h-1 bg-[color:var(--f2-border)] mb-10 overflow-hidden">
          <div
            className="h-1 bg-[color:var(--f2-saffron)] transition-all duration-500"
            style={{ width: `${progress}%` }}
            data-testid="assessment-progress-bar"
          />
        </div>

        {!bank && !err && (
          <div className="hard-border p-10 bg-[color:var(--f2-surface)] label-xs text-center">Loading assessment…</div>
        )}

        {err && (
          <div className="hard-border p-6 bg-[color:var(--f2-surface)] text-sm text-red-400" data-testid="assessment-error">{err}</div>
        )}

        {/* Step 0 — profile */}
        {bank && step === 0 && (
          <div className="hard-border p-8 bg-[color:var(--f2-surface)] space-y-4">
            <div>
              <label className="label-xs block mb-2">Your background</label>
              <textarea
                data-testid="assessment-profile-background"
                className="input-crisp min-h-[110px]"
                value={profile.background}
                onChange={(e) => setProfile({ ...profile, background: e.target.value })}
                placeholder="e.g. B.Tech Aerospace + 3 yrs BFSI analytics"
                required
              />
            </div>
            <div>
              <label className="label-xs block mb-2">Your goal in space <span className="text-[color:var(--f2-muted)]">(optional)</span></label>
              <textarea
                data-testid="assessment-profile-goals"
                className="input-crisp min-h-[80px]"
                value={profile.goals}
                onChange={(e) => setProfile({ ...profile, goals: e.target.value })}
                placeholder="e.g. Break into satellite insurance underwriting"
              />
            </div>
            <div className="flex justify-end pt-2">
              <button
                data-testid="assessment-profile-next"
                onClick={handleProfileNext}
                disabled={!profile.background.trim()}
                className="btn-crisp btn-primary"
              >
                Begin Diagnostic →
              </button>
            </div>
          </div>
        )}

        {/* Questions */}
        {bank && inQuestions && currentQ && (
          <div className="hard-border p-8 bg-[color:var(--f2-surface)]">
            <div className="label-xs text-[color:var(--f2-muted)] mb-2">
              {currentQ.type === "mcq" ? "Knowledge Check" : "Self-Assessment"}
            </div>
            <h2 className="font-display font-bold text-xl md:text-2xl mb-6">{currentQ.q}</h2>

            {currentQ.type === "likert" && (
              <div className="grid grid-cols-1 sm:grid-cols-5 gap-2">
                {LIKERT_OPTIONS.map((o) => {
                  const active = answers[currentQ.id] === o.v;
                  return (
                    <button
                      key={o.v}
                      data-testid={`assessment-answer-${currentQ.id}-${o.v}`}
                      onClick={() => handleAnswer(currentQ.id, o.v)}
                      className={`hard-border p-4 text-center transition-colors ${active ? "border-[color:var(--f2-saffron)] bg-black/40" : "hover:border-[color:var(--f2-saffron)]"}`}
                    >
                      <div className={`font-display font-black text-2xl ${active ? "saffron" : ""}`}>{o.v}</div>
                      <div className="label-xs mt-1">{o.label}</div>
                    </button>
                  );
                })}
              </div>
            )}

            {currentQ.type === "mcq" && (
              <div className="space-y-2">
                {currentQ.options.map((o) => {
                  const active = answers[currentQ.id] === o.value;
                  return (
                    <button
                      key={o.value}
                      data-testid={`assessment-answer-${currentQ.id}-${o.value}`}
                      onClick={() => handleAnswer(currentQ.id, o.value)}
                      className={`w-full text-left hard-border p-4 transition-colors ${active ? "border-[color:var(--f2-saffron)] bg-black/40" : "hover:border-[color:var(--f2-saffron)]"}`}
                    >
                      <div className="flex items-center gap-3">
                        <span className={`label-xs w-8 ${active ? "saffron" : ""}`}>{String.fromCharCode(65 + o.value)}</span>
                        <span className={`text-sm ${active ? "saffron" : ""}`}>{o.label}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            <div className="flex justify-between mt-8">
              <button
                data-testid="assessment-back-button"
                onClick={() => setStep((s) => Math.max(0, s - 1))}
                className="btn-crisp"
              >
                ← Back
              </button>
              <button
                data-testid="assessment-next-button"
                onClick={() => setStep((s) => s + 1)}
                disabled={answers[currentQ.id] === undefined}
                className="btn-crisp btn-primary"
              >
                {answers[currentQ.id] === undefined ? "Pick an answer" : "Next →"}
              </button>
            </div>
          </div>
        )}

        {/* Submit step */}
        {bank && step === totalQ + 1 && (
          <div className="hard-border p-8 bg-[color:var(--f2-surface)] space-y-4">
            <div className="label-xs">§ Ready</div>
            <h2 className="font-display font-bold text-2xl">All {totalQ} answered.</h2>
            <p className="text-[color:var(--f2-muted)]">
              We&apos;ll compute your Forte Score across six dimensions and generate a personalised AI-authored
              report grounded in India&apos;s space economy. This takes about 30 seconds.
            </p>
            {err && <div className="text-sm text-red-400" data-testid="assessment-submit-error">{err}</div>}
            <button
              data-testid="assessment-submit-button"
              onClick={submit}
              disabled={submitting}
              className="btn-crisp btn-primary"
            >
              {submitting ? "Generating your report…" : "Generate my Forte Score →"}
            </button>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
